/**
 * useWidgetContextCapture
 *
 * Add this hook to any widget that should support "Add to Chat".
 * Call `captureContext(requestPayload)` after your widget's data loads.
 * This stores the widget's request context so the user can attach it to
 * the Quick Help panel (response data is intentionally excluded — the
 * agent queries live data itself).
 *
 * Usage in a widget:
 *
 *   const { captureContext, addToChat } = useWidgetContextCapture({
 *     componentName: 'DohMetricCard',
 *     label: `DOH — ${sku.name}`,
 *   });
 *
 *   // After data loads:
 *   useEffect(() => {
 *     if (data) captureContext(requestParams);
 *   }, [data]);
 *
 *   // In your "Add to Chat" button handler:
 *   <button onClick={addToChat}>Add to Chat</button>
 *
 * IMPORTANT: Context is stored in a local ref (not in provider state) to
 * avoid race conditions when multiple widgets on the same page call
 * captureContext simultaneously. The context is passed directly to createTab
 * when addToChat is called.
 */

import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router';
import useQuickHelpContext from '@providers/QuickHelp/useQuickHelpContext';
import type { WidgetContext } from '@providers/QuickHelp/types';
import { resolvePayloadTokens } from '@utils/compute-engine';

interface UseWidgetContextCaptureOptions {
    componentName: string;
    /** Human-readable label shown in the attachment pill */
    label: string;
}

export function useWidgetContextCapture(options: UseWidgetContextCaptureOptions) {
    const { componentName, label } = options;
    const { pathname } = useLocation();
    const { state, actions } = useQuickHelpContext();

    // Store context locally — avoids shared-state races when multiple widgets
    // on the same page each call captureContext on data load.
    const capturedContextRef = useRef<WidgetContext | null>(null);

    const captureContext = useCallback(
        (requestPayload: Record<string, unknown>) => {
            capturedContextRef.current = {
                component_name: componentName,
                page: pathname,
                request_payload: resolvePayloadTokens(requestPayload),
                label,
            };
        },
        [componentName, label, pathname],
    );

    const addToChat = useCallback(() => {
        const ctx = capturedContextRef.current;
        if (!ctx) return;

        if (state.tabs.length === 0) {
            // No existing tabs — stage context then open panel.
            // openPanel auto-creates the first tab using pendingContext.
            actions.stagePendingContext(ctx);
            actions.openPanel();
        } else {
            // Has tabs — pass context directly so the new tab gets it immediately.
            actions.createTab(ctx);
        }
    }, [actions, state.tabs.length]);

    return { captureContext, addToChat };
}
